import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { grantAnalyticsConsent, denyAnalyticsConsent } from "@/lib/analytics";
import { grantPixelConsent, denyPixelConsent } from "@/lib/metaPixel";
// Cinematic brand chrome: the banner mounts at RootLayout level (outside any
// .todai-cinematic page scope), so it carries the scope class on its own root
// and imports the stylesheet itself to be theme-correct on every route.
import "@/pages/cinematic.css";
import { PRIVACY_HREF } from '@/config/platform';

const STORAGE_KEY = "cookie-consent";

/**
 * Returns true only if the user has explicitly accepted cookies.
 * Use this guard before loading any analytics, tracking pixels, or
 * third-party scripts that set non-essential cookies (GDPR compliance).
 *
 * Example:
 *   if (hasConsentedToCookies()) { loadGoogleAnalytics(); }
 */
export const hasConsentedToCookies = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "accepted";
  } catch {
    return false;
  }
};

/** Reset cookie consent so the banner reappears. */
export const resetCookieConsent = (): void => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  window.dispatchEvent(new Event('cookie-consent-reset'));
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation(["common"]);
  const location = useLocation();
  const lp = useLocalizedPath();
  const isDesigner = location.pathname.includes('/cctv-design');
  // The client portal (/p/:token) is a white-label page the installer's
  // CLIENT sees — our cookie banner must not overlay their branding.
  const isClientPortal = /^\/p\//.test(location.pathname);

  useEffect(() => {
    try {
      const consent = localStorage.getItem(STORAGE_KEY);
      if (!consent) {
        const timer = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  // Listen for reset events (triggered from footer "Cookie Settings" link)
  useEffect(() => {
    const handler = () => setVisible(true);
    window.addEventListener('cookie-consent-reset', handler);
    return () => window.removeEventListener('cookie-consent-reset', handler);
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch { /* ignore */ }
    grantAnalyticsConsent();
    grantPixelConsent();
    setVisible(false);
  };

  const decline = () => {
    try { localStorage.setItem(STORAGE_KEY, "declined"); } catch { /* ignore */ }
    denyAnalyticsConsent();
    denyPixelConsent();
    setVisible(false);
  };

  if (!visible || isDesigner || isClientPortal) return null;

  return (
    <div
      role="dialog"
      aria-label={t("cookie_eyebrow", "§ / COOKIE POLICY")}
      className="todai-cinematic cin-chrome-bar cin-chrome-bar--bottom fixed bottom-0 left-0 right-0 z-[100]"
    >
      <div className="cin-container cin-chrome-bar__inner">
        <p className="cin-chrome-bar__text">
          {t("cookie_message", "We use cookies to analyze site usage and improve your experience. You can accept or decline non-essential cookies.")}{" "}
          <Link to={PRIVACY_HREF === "/rodo" ? lp("/rodo") : PRIVACY_HREF} className="cin-chrome-bar__link">
            {t("privacy_policy", "Privacy Policy")}
          </Link>
        </p>
        <div className="cin-chrome-bar__actions">
          <button type="button" onClick={decline} className="cin-chrome-bar__ghost">
            {t("cookie_decline_cta", "DECLINE")}
          </button>
          <button type="button" onClick={accept} className="cin-btn cin-btn--primary cin-chrome-bar__cta">
            {t("cookie_accept_cta", "ACCEPT →")}
          </button>
        </div>
      </div>
    </div>
  );
};
